import React from "react";
import { useMap } from "react-leaflet";
import L from "leaflet";
import * as turf from "@turf/turf";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { ListGroup, Offcanvas, Table } from "react-bootstrap";

import { circleRadius, statuteMile } from "../config";
import variableColors from "../_App.module.scss";
import { AllSelectedRoutesPolylines, InfoPopup } from "./helpers/PolylinesComponents";
import useAxios from "../hooks/useAxios";
import { AgencyContext } from "../hooks/AgencyContext";

const { pinkColor, indigoColor } = variableColors;

const ListDiv = styled.div`
  width: 30vw;
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-content: center;
  align-items: center;
  cursor: pointer;
  gap: 0.5rem;
  span {
    text-align: center;
    min-width: 4rem;
    font-weight: bold;
  }
`;

const MapAllRoutes = ({ agencyId }: { agencyId: string }): JSX.Element => {
  const { t } = useTranslation();
  const leafletMap = useMap();

  //* Custom Hook
  const { allRoutes, multiPolylines } = useAxios(agencyId);

  //* AgencyContext
  const { speedUnit } = React.useContext(AgencyContext) as AgencyContextType;

  const [showOffCanvas, setShowOffCanvas] = React.useState<boolean>(false);
  const [selectedRoutes, setSelectedRoutes] = React.useState<ObjectI[]>([]);
  const [routeToShow, setRouteToShow] = React.useState<string>("");

  //* Reset Routes
  const resetSelectedRoutes = (): void => {
    setSelectedRoutes([]);
    setRouteToShow("");
  };

  //* Close OffCanvas
  const handleCloseOffCanvas = (): void => {
    setShowOffCanvas(false);
    resetSelectedRoutes();
  };

  //- Close OffCanvas if no routeToShow and no selectedRoutes
  React.useEffect(() => {
    if (!routeToShow && selectedRoutes.length === 0) {
      setShowOffCanvas(false);
    }
  }, [routeToShow, selectedRoutes]);

  //- Displaying nearest routes
  React.useEffect(() => {
    if (!leafletMap) return;
    if (leafletMap && multiPolylines?.length && allRoutes?.length) {
      leafletMap.on("click", async (event: L.LeafletMouseEvent) => {
        await resetSelectedRoutes();

        const { lat, lng } = event?.latlng;
        // console.log({ lat, lng });
        const circleMarker = L.circle([lat, lng], {
          radius: circleRadius,
          stroke: true,
          weight: 3,
          fillColor: pinkColor,
          color: indigoColor,
          opacity: 1,
          fillOpacity: 0.2,
        }).addTo(leafletMap);

        const turfCircle = turf.circle([lng, lat], circleRadius, { steps: 10, units: "meters" });
        // console.log("turfCircle:", turfCircle);

        const routesIdsArray = [] as string[];
        multiPolylines?.forEach((multiPolyline: MultiPolylineObj) => {
          if ((turf as any).booleanIntersects(turfCircle, turf.multiLineString(multiPolyline?.shapes))) {
            // console.log("multiPolyline.id:", multiPolyline.id);
            routesIdsArray.push(multiPolyline.id);
          }
        });
        const routesIdsReduces = [...new Set(routesIdsArray)]?.sort((a: string, b: string) => a.localeCompare(b));
        // console.log("routesIdsReduces:", routesIdsReduces);
        const clickedRoutes: ObjectI[] = multiPolylines
          ?.filter((elem: MultiPolylineObj) => routesIdsReduces?.includes(elem?.id))
          ?.map((elem: MultiPolylineObj) => {
            return {
              id: elem?.id,
              name: elem?.name,
              longName: elem?.longName,
              shortName: elem?.shortName,
              acronym: elem?.acronym || "",
            };
          });
        // console.log("clickedRoutes:", clickedRoutes);
        setSelectedRoutes(clickedRoutes);

        setTimeout(() => {
          circleMarker.removeFrom(leafletMap);
        }, 750);
      });
    }
  }, [allRoutes, leafletMap, multiPolylines]);

  //* Open OffCanvas if selectedRoutes
  React.useEffect(() => {
    if (selectedRoutes && selectedRoutes?.length) {
      setShowOffCanvas(true);
      if (selectedRoutes?.length === 1) {
        setRouteToShow(selectedRoutes?.[0]?.id as string);
      }
    }
  }, [selectedRoutes]);

  //* OffCanvas: Select Route Component
  const SelectRoute = (): JSX.Element => {
    const listToSelectRoute: JSX.Element[] = selectedRoutes?.map((route: ObjectI, index: number) => {
      return (
        <ListGroup.Item key={`${index}_${route?.id}`} onClick={() => setRouteToShow(route?.id as string)}>
          <ListDiv>
            <span
              style={{
                background: allRoutes?.filter((elem) => elem?.id === route?.id)?.[0]?.background,
                color: allRoutes?.filter((elem) => elem?.id === route?.id)?.[0]?.color,
                padding: "0.25rem 0.5rem",
              }}
            >
              {route?.acronym || route?.shortName || route?.id}
            </span>{" "}
            {`${String.fromCharCode(8658)} ${route?.longName || route?.name || route?.shortName}`}
          </ListDiv>
        </ListGroup.Item>
      );
    });
    return <ListGroup>{listToSelectRoute}</ListGroup>;
  };

  //* OffCanvas: Selected Route Info Component
  const SelectedRouteInfo = ({ selectedRouteId }: { selectedRouteId: string }): JSX.Element => {
    const routeObj: RouteObj = allRoutes?.filter((elem: RouteObj) => elem?.id === selectedRouteId)?.[0];
    const shapesDirections: Direction[] = [...routeObj?.shapesDirection_0!, ...routeObj?.shapesDirection_1!];

    return (
      <InfoPopup $width="30rem">
        <p>
          {t("Id")}{" "}
          <span style={{ background: routeObj?.background, color: routeObj?.color, padding: "0.25rem 0.5rem" }}>
            {routeObj?.acronym || routeObj?.shortName || routeObj?.id || t("b/d")}
          </span>
        </p>
        <p>
          {t("Nazwa")} <span>{routeObj?.name || t("b/d")}</span>
        </p>
        <br />

        <Table striped bordered hover size="sm" className="bus_table2">
          <thead>
            <tr>
              <th>#</th>
              <th>{t("Dokąd")}</th>
              <th>{t("Długość")}</th>
              <th>{t("Kierunek")}</th>
            </tr>
          </thead>
          <tbody>
            {shapesDirections?.map((shapeDirection: Direction, index: number): JSX.Element => {
              return (
                <tr key={index}>
                  <td>{index + 1}</td>
                  <td>{shapeDirection?.headsign || t("b/d")}</td>
                  <td>
                    {speedUnit === "kmh"
                      ? `${(shapeDirection?.length / 1000).toFixed(2)} km`
                      : speedUnit === "mph"
                      ? `${(shapeDirection?.length / (1000 * statuteMile)).toFixed(2)} miles`
                      : t("b/d")}
                  </td>
                  <td>{shapeDirection?.directionId || t("b/d")}</td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </InfoPopup>
    );
  };

  // RouteInfoOffCanvas Component
  const RouteInfoOffCanvas = (): JSX.Element => {
    return (
      <React.Fragment>
        <Offcanvas
          show={showOffCanvas}
          onHide={handleCloseOffCanvas}
          backdrop={false}
          scroll={false}
          keyboard={true}
          placement="start"
          className="w-auto"
        >
          <Offcanvas.Header closeButton={true} closeLabel="Close">
            <Offcanvas.Title as="h5">{routeToShow ? t("Szczegóły trasy") : t("Wybierz trasę")}</Offcanvas.Title>
          </Offcanvas.Header>
          <Offcanvas.Body>
            {routeToShow ? (
              <React.Fragment>
                <SelectedRouteInfo selectedRouteId={routeToShow} />
              </React.Fragment>
            ) : (
              <React.Fragment>
                <SelectRoute />
              </React.Fragment>
            )}
          </Offcanvas.Body>
        </Offcanvas>
      </React.Fragment>
    );
  };

  return (
    <React.Fragment>
      {/* //* All Routes */}
      {allRoutes?.length ? (
        <AllSelectedRoutesPolylines selectedRoutes={allRoutes} showPopup={false} routeToShow={routeToShow} />
      ) : null}

      {/* //* OffCanvas */}
      {RouteInfoOffCanvas()}
    </React.Fragment>
  );
};

export default MapAllRoutes;
